import { InjectionKey, WatchSource } from 'vue';
import type { InternalApi } from 'nitropack/types';
import { useItems } from './data';
import { match } from "ts-pattern";


export type ICPSignalTask = {
    title: string,
    description?: string,
    slow?: boolean,
}
export const ICPConfigKey = Symbol() as InjectionKey<{
    edited: (requestPreview: boolean, itemId?: number) => Promise<void>;
}>;

export interface ContactTableListener {
    (tableIds: number[]): void;
}
export const ContactTableRefreshKey = Symbol() as InjectionKey<{
    update: (tableIds: number[]) => void;
    addUpdateListener: (listener: ContactTableListener) => { (): void };
}>;

export type PreviewCompany = InternalApi['/api/icps/:id/preview']['post']['companies'][number];
export type ICPValidationAccountMode = 'preview' | 'total' | 'qualified' | 'disqualified';


/**
 * Interface for the result of useICPItemInteraction
 */
export interface ICPItemInteractionState {
    // Current state
    itemId: Ref<number | undefined>;
    mode: Ref<ICPValidationAccountMode>;
    loadingPreview: Ref<boolean>;
    previewTasks: Ref<ICPSignalTask[]>;
    page: Ref<number>;

    // Data
    previewCompanies: Ref<PreviewCompany[]>;
    testProspects: Ref<any[]>;
    testProspectsCount: Ref<number>;
    potentialCompanies: Ref<number>;
    resultIds: Ref<Record<number, Record<number, number>>>;

    // Actions
    focusItem: (itemId?: number, preferredMode?: ICPValidationAccountMode) => Promise<void>;
}

/**
 * Extract queries from item parameters
 */
function extractQueriesFromParam(params: any): ResourceQuery[] {
    if ("queries" in params) return params.queries as ResourceQuery[];
    if ("resources" in params) return params.resources as ResourceQuery[];
    return [];
}

/**
 * Composable for managing ICP item interactions including focus, editing, and testing
 */
export function useICPItemInteraction(icpId: number): ICPItemInteractionState {
    const { data: icp } = useICP(icpId);
    const itemIdsGetter = () => icp.value?.itemIds ?? [];

    // State management
    const focusItemId = ref<number>();
    const mode = ref<ICPValidationAccountMode>('preview');
    const page = ref(1);
    const loadingPreview = ref(false);
    const previewTasks = ref<ICPSignalTask[]>([]);
    const pageCount = 10;

    // Data containers
    const previewCompanies = ref<PreviewCompany[]>([]);
    const testProspects = ref<any[]>([]);
    const testProspectsCount = ref<number>(0);
    const potentialCompanies = ref<number>(0);
    const resultIds: Ref<Record<number, Record<number, number>>> = ref({});

    /**
     * Load preview data for the given item
     */
    async function loadPreviewData(itemId?: number) {
        loadingPreview.value = true;
        try {
            // When no itemId is provided (Search Accounts), we need to load general preview data
            const data = await $fetch(`/api/icps/${icpId}/preview`, { method: 'POST', body: {
                highlightItemId: itemId,
            } });

            previewCompanies.value = data.companies || [];
            potentialCompanies.value = 0;

            // Clear preview tasks when viewing search accounts
            if (!itemId) {
                previewTasks.value = [];
            }
        } finally {
            loadingPreview.value = false;
        }
    }

    /**
     * Load tasks related to an item
     */
    const { data: items } = useItems();
    async function loadItemTasks(itemId: number) {
        const item = items.value.find(i => i.id === itemId);
        if (!item) return;

        const tasks = [] as ICPSignalTask[];
        const queries = extractQueriesFromParam(item.parameters);

        for (const query of queries) {
            if (query.providerId === employeeProviderId) {
                tasks.push({
                    title: "Search employees using provided titles",
                    slow: true,
                });
            } else if (query.providerId === companyPageProviderId) {
                tasks.push({
                    title: "Scrape pages from the company website",
                    slow: true,
                });
            }
        }

        const params = item.parameters;
        if (params && typeof params === 'object' && "question" in params) {
            const question = params.question;
            tasks.push({
                title: "Ask AI",
                description: typeof question === 'string' ? question : undefined,
            });
        }

        previewTasks.value = tasks;
    }

    /**
     * Load test prospects with pagination and filtering
     */
    async function loadTestProspects() {
        if (!focusItemId.value) return;

        const pageSize = 10
        const status = match(mode.value)
            .with("preview", () => "total" as const)
            .otherwise(val => val);
        const data = await $fetch(`/api/icps/${icpId}/prospects/test-results`, { method: 'POST', body: {
            itemId: focusItemId.value,
            status,
            take: pageSize,
            skip: (page.value - 1) * pageSize,
        } });

        testProspects.value = data.prospects;
        testProspectsCount.value = data.count;

        // Load result IDs for the prospects
        if (testProspects.value.length > 0) {
            resultIds.value = await $fetch('/api/results/resolve', { method: 'POST', body: {
                prospectIds: testProspects.value.map(p => p.id),
                itemIds: itemIdsGetter(),
            } });
        }
    }

    /**
     * Focus on a specific item and load appropriate data
     */
    async function focusItem(itemId?: number, preferredMode?: ICPValidationAccountMode) {
        // If undefined is passed, it means we want to focus on the search block
        if (itemId === undefined) {
            focusItemId.value = undefined;
            mode.value = 'preview';
            await loadPreviewData();
            return;
        }

        if (focusItemId.value != itemId) focusItemId.value = itemId;

        // First try to load test results, if none available then show preview
        await loadTestProspects();

        console.log(`preferred mode: ${preferredMode || testProspects.value.length > 0 ? 'total' : 'preview'}`)
        mode.value = preferredMode || (testProspects.value.length > 0 ? 'total' : 'preview');

        console.log(`test prospects count: ${testProspects.value.length}`);
        console.log(`mode.value: ${mode.value}`)

        if (mode.value === 'preview') {
            await loadPreviewData(itemId);
            await loadItemTasks(itemId);
        }
    }

    watch(page, () => loadTestProspects(), { immediate: false });
    watch(mode, async (mode) => {
        if (mode === "preview") return;
        page.value = 1;
        await loadTestProspects()
    }, { immediate: false });
    usePageInterval(async () => {
        if (mode.value === "preview") return;
        await loadTestProspects();
    }, { interval: 2000, immediate: true })

    onBeforeMount(() => focusItem());

    // Return the state and methods
    return {
        // State
        mode,
        itemId: focusItemId,
        loadingPreview,
        previewTasks,
        page,

        // Data
        previewCompanies,
        testProspects,
        testProspectsCount,
        potentialCompanies,
        resultIds,

        // methods
        focusItem,
    };
}

/**
 * Interface for validation status information
 */
export interface ValidationStatus {
    status: string;
    exploredCount: number;
}

/**
 * Interface for the validation status state
 */
export interface ValidationStatusState {
    // Data
    validationStatuses: Ref<Record<number, ValidationStatus>>;

    // Methods
    fetchValidationStatus: () => Promise<void>;
    isValidating: (itemId: number) => boolean;
}

/**
 * Composable for managing validation status of items in an ICP
 * @param icpId The ID of the ICP
 * @param itemIdsGetter Function that returns the current item IDs to track
 * @returns ValidationStatusState object
 */
export function useValidationStatus(icpId: number): ValidationStatusState {
    const { data: icp } = useICP(icpId);
    const itemIdsGetter = () => icp.value?.itemIds ?? [];

    // State tracking
    const validationStatuses = ref<Record<number, ValidationStatus>>({});

    // Fetch validation status
    async function fetchValidationStatus() {
        try {
            const statuses = await $fetch('/api/icps/items/validation-status', { method: 'POST', body: {
                icpId,
                itemIds: itemIdsGetter()
            } });
            validationStatuses.value = statuses.reduce((acc, status) => {
                acc[status.itemId] = {
                    status: status.status,
                    exploredCount: status.exploredCount
                };
                return acc;
            }, {} as Record<number, ValidationStatus>);
        } catch (error) {
            console.error('Failed to fetch validation status', error);
        }
    }

    // Setup interval for refreshing validation status
    usePageInterval(async () => {
        await fetchValidationStatus();
    }, { interval: 2000, immediate: true });

    // Check if an item is currently being validated
    function isValidating(itemId: number): boolean {
        const status = validationStatuses.value[itemId]?.status || 'idle';
        return ['queuing', 'in-progress'].includes(status);
    }

    return {
        validationStatuses,
        fetchValidationStatus,
        isValidating
    };
}


export function usePotentialCountAudits(icpId: number) {
    const baseCount = ref(0);
    const filterCounts = ref<Record<number, number>>({});
    const refresh = async () => {
        const auditResult = await $fetch('/api/icps/audit/potential-counts', { query: {
            icpId,
        } });
        baseCount.value = auditResult.baseCount;
        filterCounts.value = auditResult.filterCounts.reduce((acc, count) => {
            acc[count.itemId] = count.count;
            return acc;
        }, {} as Record<number, number>);
    }

    onBeforeMount(async () => {
        await refresh();
    })

    return {
        baseCount,
        filterCounts,
        refresh,
    }
}

export function useResultIds(prospectIds: WatchSource<number[]>, itemIds: WatchSource<number[]>) {
    const resultIds: Ref<Record<number, Record<number, number>>> = ref({});
    const getValueFromWatchSource = (source: WatchSource) => {
        if ("value" in source) return source.value;
        return source();
    }
    const loadResultIds = async () => {
        resultIds.value = await $fetch('/api/results/resolve', { method: 'POST', body: {
            prospectIds: getValueFromWatchSource(prospectIds),
            itemIds: getValueFromWatchSource(itemIds),
        } })
    }
    watch(prospectIds, async () => {
        await loadResultIds();
    })
    watch(itemIds, async () => {
        await loadResultIds();
    })
    return resultIds;
}

export interface AIItemParamModifiedHook {
    (itemId: number): Promise<void> | void
}

type RefreshHook = () => Promise<void> | void;

const ICPHooksKey = Symbol() as InjectionKey<{
    registerAIItemParamModifiedHook: (hook: AIItemParamModifiedHook) => void,
    aiModifyItemParams: (itemId: number) => Promise<void>,
    registerRefreshSequencesHook: (hook: RefreshHook) => () => void,
    refreshSequences: () => Promise<void>,
}>;

export function useICPHooksProvider() {
    let aiItemParamModifiedHooks: AIItemParamModifiedHook[] = [];
    let refreshSequencesHooks: RefreshHook[] = [];
    provide(ICPHooksKey, {
        registerAIItemParamModifiedHook: (hook: AIItemParamModifiedHook) => {
            aiItemParamModifiedHooks.push(hook);

            return () => {
                const idx = aiItemParamModifiedHooks.indexOf(hook);
                if (idx > -1) aiItemParamModifiedHooks.splice(idx, 1);
            }
        },
        aiModifyItemParams: async (itemId: number) => {
            for (const hook of aiItemParamModifiedHooks) {
                await hook(itemId);
            }
        },
        registerRefreshSequencesHook: (hook: RefreshHook) => {
            refreshSequencesHooks.push(hook);
            return () => {
                const idx = refreshSequencesHooks.indexOf(hook);
                if (idx > -1) refreshSequencesHooks.splice(idx, 1);
            }
        },
        refreshSequences: async () => {
            for (const hook of refreshSequencesHooks) {
                await hook();
            }
        },
    })
}

export function useICPHooks() {
    const hooks = inject(ICPHooksKey, undefined);
    return {
        aiModifyItemParams: hooks?.aiModifyItemParams,
        onAIItemParamModified: (hook: AIItemParamModifiedHook) => {
            const cleanup = hooks?.registerAIItemParamModifiedHook(hook);
            if (cleanup) {
                onUnmounted(cleanup);
            }
        },
        refreshSequences: hooks?.refreshSequences,
        onRefreshSequences: (hook: RefreshHook) => {
            const cleanup = hooks?.registerRefreshSequencesHook(hook);
            if (cleanup) {
                onUnmounted(cleanup);
            }
        },
    };
}

/**
 * Interface for pending changes management
 */
export interface PendingChangesState {
    hasPendingChanges: (itemId?: number) => boolean;
    registerPendingChange: (itemId: number, flushFn: () => Promise<void>) => void;
    unregisterPendingChange: (itemId: number) => void;
    flushPendingChanges: (itemId?: number) => Promise<void>;
    flushAllPendingChanges: () => Promise<void>;
}

/**
 * Injection key for pending changes management
 */
export const PendingChangesKey = Symbol() as InjectionKey<PendingChangesState>;

/**
 * Provider composable for managing pending changes across components
 * Use this at the parent level (e.g., in setup.vue)
 */
export function usePendingChangesProvider(): PendingChangesState {
    const pendingChanges = ref<Map<number, () => Promise<void>>>(new Map());
    const flushingChanges = ref<Set<number>>(new Set());

    function hasPendingChanges(itemId?: number): boolean {
        if (itemId !== undefined) {
            return pendingChanges.value.has(itemId) || flushingChanges.value.has(itemId);
        }
        return pendingChanges.value.size > 0 || flushingChanges.value.size > 0;
    }

    function registerPendingChange(itemId: number, flushFn: () => Promise<void>): void {
        pendingChanges.value.set(itemId, flushFn);
    }

    function unregisterPendingChange(itemId: number): void {
        pendingChanges.value.delete(itemId);
        flushingChanges.value.delete(itemId);
    }

    async function flushPendingChanges(itemId?: number): Promise<void> {
        if (itemId !== undefined) {
            const flushFn = pendingChanges.value.get(itemId);
            if (flushFn) {
                flushingChanges.value.add(itemId);
                pendingChanges.value.delete(itemId);
                try {
                    await flushFn();
                } finally {
                    flushingChanges.value.delete(itemId);
                }
            }
        } else {
            // Flush all if no specific itemId provided
            await flushAllPendingChanges();
        }
    }

    async function flushAllPendingChanges(): Promise<void> {
        const allFlushFns = Array.from(pendingChanges.value.entries());

        // Mark all as flushing
        for (const [itemId] of allFlushFns) {
            flushingChanges.value.add(itemId);
        }

        // Clear pending map
        pendingChanges.value.clear();

        // Execute all flush functions
        try {
            await Promise.all(allFlushFns.map(([_, flushFn]) => flushFn()));
        } finally {
            // Clear flushing set
            flushingChanges.value.clear();
        }
    }

    const state: PendingChangesState = {
        hasPendingChanges,
        registerPendingChange,
        unregisterPendingChange,
        flushPendingChanges,
        flushAllPendingChanges
    };

    provide(PendingChangesKey, state);

    return state;
}

/**
 * Consumer composable for using pending changes management
 * Use this in child components that need to interact with pending changes
 */
export function usePendingChanges(): PendingChangesState {
    const state = inject(PendingChangesKey);
    if (!state) {
        throw new Error('usePendingChanges must be used within a component that has called usePendingChangesProvider');
    }
    return state;
}